/**
 * Turns the `/notification-settings/get-global` envelope into the four
 * ordered groups the screen draws, and owns the kill-switch dimming rule and
 * the locked-channel caption, so both are tested without rendering anything.
 * Which channels it draws comes from `WEB_SETTINGS_CHANNELS`, the same list
 * the per-pool screen uses.
 */
import {
  ALERT_GROUP_HEADERS,
  ALERT_GROUP_ORDER,
  ALERT_KIND_LABELS,
  type typeAlertGroup,
  type typeAlertKind,
  type typeAlertRow,
  type typeChannel,
} from "./alertKindLabels";
import { WEB_SETTINGS_CHANNELS } from "./webSettingsChannels";

export type typeChannelCell = {
  channel: typeChannel;
  isEnabled: boolean;
  isLocked: boolean;
  /** True when the account-wide email kill switch dims this cell. */
  isDimmed: boolean;
  /** "Always on." | "Always off." | undefined */
  caption: string | undefined;
};

export type typeAlertRowModel = {
  alertKind: typeAlertKind;
  label: string;
  cells: typeChannelCell[];
};

export type typeAlertGroupModel = {
  group: typeAlertGroup;
  header: string;
  rows: typeAlertRowModel[];
};

export type typeNotificationSettingsState =
  | { phase: "loading" }
  | { phase: "failed"; errorCode: string | null }
  | {
      phase: "ready";
      groups: typeAlertGroupModel[];
      isEmailKillSwitchOn: boolean;
    };

function buildCell(
  channel: typeChannel,
  cell: { isEnabled: boolean; isLocked: boolean },
  isEmailKillSwitchOn: boolean,
): typeChannelCell {
  const caption = cell.isLocked
    ? cell.isEnabled
      ? "Always on."
      : "Always off."
    : undefined;
  return {
    channel,
    isEnabled: cell.isEnabled,
    isLocked: cell.isLocked,
    isDimmed: channel === "email" && isEmailKillSwitchOn,
    caption,
  };
}

export function resolveNotificationSettingsState(input: {
  isLoading: boolean;
  hasFailed: boolean;
  errorCode: string | null;
  alerts: readonly typeAlertRow[] | null;
  isEmailKillSwitchOn: boolean;
}): typeNotificationSettingsState {
  const { isLoading, hasFailed, errorCode, alerts, isEmailKillSwitchOn } =
    input;

  if (hasFailed) return { phase: "failed", errorCode };
  if (isLoading || alerts === null) return { phase: "loading" };

  const rowsByGroup = new Map<typeAlertGroup, typeAlertRowModel[]>();
  for (const row of alerts) {
    const model: typeAlertRowModel = {
      alertKind: row.alertKind,
      label: ALERT_KIND_LABELS[row.alertKind],
      cells: WEB_SETTINGS_CHANNELS.map((channel) =>
        buildCell(channel, row[channel], isEmailKillSwitchOn),
      ),
    };
    const existing = rowsByGroup.get(row.group);
    if (existing) {
      existing.push(model);
    } else {
      rowsByGroup.set(row.group, [model]);
    }
  }

  const groups: typeAlertGroupModel[] = [];
  for (const group of ALERT_GROUP_ORDER) {
    const rows = rowsByGroup.get(group);
    if (rows === undefined || rows.length === 0) continue;
    groups.push({
      group,
      header: ALERT_GROUP_HEADERS[group],
      rows,
    });
  }

  return { phase: "ready", groups, isEmailKillSwitchOn };
}
